<script setup lang="ts">
import { ref, PropType, watchEffect, computed} from 'vue';
import { AnyTile } from '@bc/discovery/domain/catalog';
import { partition } from '@core/utilities/lodash-utils';
import { GridSlot, GridConfig } from './types';

defineOptions({ name: 'CatalogGrid' });

const props = defineProps({
  tiles: {
    type: Array as PropType<AnyTile[]>,
    required: true,
  },
  tilesPerRow: {
    type: Number,
    required: true,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  isSearching: {
    type: Boolean,
    required: true,
  },
  config: {
    type: Object as PropType<GridConfig>,
    default: undefined,
    validator: (value: GridConfig) => {
      return typeof value === 'undefined' || Object.values(value).every((v) => v.row > 0);
    },
  },
  filtersShown: {
    type: Boolean,
    default: false,
  },
});

const slots = ref<{ injection: GridSlot[]; footer: GridSlot[] }>({
  injection: [],
  footer: [],
});

watchEffect(() => {
  if (!props.config) {
    slots.value = {
      injection: [],
      footer: [],
    };
    return;
  }
  const allSlots = Object.entries(props.config).map(([key, value], index) => {
    return {
      name: key,
      index: props.tilesPerRow * (value.row - 1) - props.tilesPerRow * index,
    };
  });
  const [injection, footer] = partition(allSlots, (slot) => slot.index < props.tiles.length);
  slots.value = { injection, footer };
});

const getInjectedSlots = (index: number) => slots.value.injection.filter((slot) => slot.index === index);

const columns = computed(() => `${props.tilesPerRow}`);
</script>

<template>
  <div
    :class="{
      'catalog-grid': true,
      'catalog-grid--with-new-tiles': $isEnabled('RET-3436'),
    }"
    data-testid="catalog-grid"
  >
    <template v-if="hasError">
      <slot name="error" />
    </template>
    <template v-else-if="isSearching">
      <template
        v-for="i in 20"
        :key="i"
      >
        <slot name="skeleton" />
      </template>
    </template>
    <template v-else-if="Boolean(tiles.length)">
      <template
        v-for="(tile, index) in tiles"
        :key="index"
      >
        <template v-if="getInjectedSlots(index).length">
          <div
            v-for="slot in getInjectedSlots(index)"
            :key="slot.name"
            class="catalog-grid__injection"
            data-testid="catalog-grid-injection"
            :data-testindex="index"
          >
            <slot :name="slot.name" />
          </div>
        </template>
        <div
          class="ds-h-full"
          data-testid="catalog-grid-tile"
        >
          <slot
            :tile="tile"
            :index="index"
          />
        </div>
      </template>
      <slot name="footer">
        <template
          v-for="(item, index) in slots.footer"
          :key="index + item.name"
        >
          <div
            v-if="item"
            class="catalog-grid__injection"
          >
            <slot :name="item.name" />
          </div>
        </template>
      </slot>
    </template>
    <template v-else>
      <div class="ds-col-span-5">
        <slot name="noResults" />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use '@css/abstracts/design-tokens' as d;

.catalog-grid {
  display: grid;
  grid-template-columns: repeat(v-bind(columns), minmax(0, 1fr));
  gap: 1rem;

  &__injection {
    grid-column: span v-bind(columns) / span v-bind(columns);
  }

  @include d.md() {
    gap: 1.25rem;
  }

  &--with-new-tiles {
    column-gap: 1rem;
    row-gap: 1.5rem;
    @include d.md() {
      column-gap: 1.25rem;
    }
  }
}
</style>
